import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import PrivateRoute from './shared/components/PrivateRoute';
import SessionTimeout from './shared/components/SessionTimeout';
import MainLayout from './shared/layout/MainLayout';
import ErrorBoundary from './shared/components/ErrorBoundary';
import {
  AlertsErrorBoundary,
  IncidentsErrorBoundary,
  DashboardErrorBoundary,
} from './shared/components/FeatureErrorBoundary';
import { ThemeProvider } from './shared/context/ThemeContext';
import LoadingSpinner from './shared/components/LoadingSpinner';
import lazyWithRetry from './shared/utils/lazyWithRetry';

import SsoProvider from './shared/auth/SsoProvider';

import './shared/styles/global.css';

// Eager: the three pages an unauthenticated visitor can reach. /callback in
// particular is mid-auth-exchange with no session, which is the worst moment
// to depend on a further network round trip for a chunk.
import LoginPage from './features/auth/LoginPage';
import CallbackPage from './features/auth/CallbackPage';
import SessionExpiredPage from './features/auth/SessionExpiredPage';
// Lazy: every protected page. Each becomes its own chunk, so a visitor at
// /login no longer downloads the incident, finance and dashboard trees. See
// shared/utils/lazyWithRetry for how a chunk stranded by a deploy recovers.
const DashboardPage = lazyWithRetry(() => import('./features/dashboard/DashboardPage'));
const DashboardPageV2 = lazyWithRetry(() => import('./features/dashboard/DashboardPageV2'));
const DashboardDrilldownPage = lazyWithRetry(() => import('./features/dashboard/DashboardDrilldownPage'));
const PresentationPage = lazyWithRetry(() => import('./features/dashboard/PresentationPage'));
const BusinessTransactionsPage = lazyWithRetry(() => import('./features/business-transactions/BusinessTransactionsPage'));
const BusinessTransactionDetailPage = lazyWithRetry(() => import('./features/business-transactions/BusinessTransactionDetailPage'));
const AlertsPage = lazyWithRetry(() => import('./features/alerts/AlertsPage'));
const AlertDetailPage = lazyWithRetry(() => import('./features/alerts/AlertDetailPage'));
const IncidentsPage = lazyWithRetry(() => import('./features/incidents/IncidentsPage'));
const IncidentDetailPage = lazyWithRetry(() => import('./features/incidents/IncidentDetailPage'));
const CreateIncidentPage = lazyWithRetry(() => import('./features/incidents/CreateIncidentPage'));
const PostIncidentReviewsPage = lazyWithRetry(() => import('./features/incidents/PostIncidentReviewsPage'));
// import GithubPrsPage from './features/github-prs/GithubPrsPage';
const MainDashboardPage = lazyWithRetry(() => import('./features/dashboard/MainDashboardPage'));
const FinanceDashboardPage = lazyWithRetry(() => import('./features/finance-metering/FinanceDashboardPage'));
const FinanceRecordsPage = lazyWithRetry(() => import('./features/finance-metering/FinanceRecordsPage'));
const OrganisationsPage = lazyWithRetry(() => import('./features/organisations/OrganisationsPage'));
const OrganisationDetailPage = lazyWithRetry(() => import('./features/organisations/OrganisationDetailPage'));
const TenantDetailPage = lazyWithRetry(() => import('./features/organisations/TenantDetailPage'));


/**
 * Root component — defines all app routes.
 * Protected routes require a valid JWT token (enforced by PrivateRoute).
 * SessionTimeout monitors token expiry across all protected pages.
 *
 * SsoProvider wraps the Router rather than sitting inside it: it provides no
 * routes of its own, and CallbackPage needs both it and useNavigate.
 *
 * Structure mirrors ecoh-ui-module's App.js, our reference for UI patterns:
 *
 *   ErrorBoundary  →  nothing can blank the screen without saying why
 *     Routes
 *       public routes
 *       PrivateRoute + MainLayout  →  guard and chrome declared once
 *         feature boundary + page   →  a crash costs one feature, not the app
 *
 * The layout route replaces what used to be <SessionTimeout /> and <Navbar />
 * hand-copied into all sixteen protected routes.
 *
 * Note for whoever needs them next: PrEventsPage, PrEventDetailPage and
 * FileUploadPage were imported here but had no routes, so they were unreachable
 * and only added weight to the bundle. The imports are gone; the features are
 * untouched under src/features/ and need a <Route> to come back.
 */
function App() {
  return (
    <ThemeProvider>
    <SsoProvider>
    <Router>
      {/* Last line of defence: a render error anywhere shows a message with a
          retry rather than the empty page that React leaves behind by default. */}
      <ErrorBoundary componentName="Application">
      {/*
        Outer net for the lazy routes that render outside MainLayout — today
        just /presentation. Routes inside MainLayout suspend against the nearer
        boundary in that shell, which keeps the sidebar on screen; this one only
        catches what that shell does not wrap.
      */}
      <Suspense fallback={<LoadingSpinner message="Loading" />}>
      <Routes>

        {/* Public routes.
            /callback MUST stay outside PrivateRoute. The user arriving back from
            Microsoft has no session yet — that is the entire point of the
            request — so a route guard here would redirect them to /login and
            discard the authorization code before it could be exchanged. */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/session-expired" element={<SessionExpiredPage />} />

        {/* Protected routes — require a JWT token, and share one shell.
            Each feature carries its own boundary so a crash in Alerts leaves the
            sidebar and every other feature usable. */}
        <Route element={<PrivateRoute><MainLayout /></PrivateRoute>}>

          <Route
            path="/dashboard"
            element={<DashboardErrorBoundary><MainDashboardPage /></DashboardErrorBoundary>}
          />
          <Route
            path="/dashboard/drilldown"
            element={<DashboardErrorBoundary><DashboardDrilldownPage /></DashboardErrorBoundary>}
          />
          <Route
            path="/dashboard-classic"
            element={<DashboardErrorBoundary><DashboardPage /></DashboardErrorBoundary>}
          />
          <Route
            path="/dashboard-v2"
            element={<DashboardErrorBoundary><DashboardPageV2 /></DashboardErrorBoundary>}
          />

          <Route path="/business-transactions" element={<BusinessTransactionsPage />} />
          <Route path="/business-transactions/:id" element={<BusinessTransactionDetailPage />} />

          <Route
            path="/alerts"
            element={<AlertsErrorBoundary><AlertsPage /></AlertsErrorBoundary>}
          />
          <Route
            path="/alerts/:alertId"
            element={<AlertsErrorBoundary><AlertDetailPage /></AlertsErrorBoundary>}
          />

          <Route
            path="/incidents"
            element={<IncidentsErrorBoundary><IncidentsPage /></IncidentsErrorBoundary>}
          />
          <Route
            path="/incidents/create/from-alert/:alertId"
            element={<IncidentsErrorBoundary><CreateIncidentPage /></IncidentsErrorBoundary>}
          />
          <Route
            path="/incidents/:service/:incidentId"
            element={<IncidentsErrorBoundary><IncidentDetailPage /></IncidentsErrorBoundary>}
          />
          <Route
            path="/post-incident-reviews"
            element={<IncidentsErrorBoundary><PostIncidentReviewsPage /></IncidentsErrorBoundary>}
          />

          {/* <Route path="/github-prs" element={<GithubPrsPage />} /> */}

          <Route path="/organisations" element={<OrganisationsPage />} />
          <Route path="/organisations/:organisationId" element={<OrganisationDetailPage />} />
          <Route path="/tenants/:tenantId" element={<TenantDetailPage />} />

          <Route path="/finance/dashboard" element={<FinanceDashboardPage />} />
          <Route path="/finance/records" element={<FinanceRecordsPage />} />

        </Route>

        {/* Protected but deliberately outside MainLayout: the presentation view
            is a wallboard, and the sidebar would only take up screen. */}
        <Route
          path="/presentation"
          element={
            <PrivateRoute>
              <SessionTimeout />
              <PresentationPage />
            </PrivateRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />

      </Routes>
      </Suspense>
      </ErrorBoundary>
    </Router>
    </SsoProvider>
    </ThemeProvider>
  );
}

export default App;
